import React from 'react';
import { InvitationDesign } from '../../../_types/invitation-design-v2';

interface LayoutTabProps {
  design: InvitationDesign;
  onTemplateUpdate: (updates: Partial<typeof design.template>) => void;
}

const LayoutTab: React.FC<LayoutTabProps> = ({ design, onTemplateUpdate }) => {
  return (
    <div className="space-y-6">
      {/* Page Margins */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-4">Page Margins</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Top</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="100"
                value={design.template.margins.top}
                onChange={(e) => onTemplateUpdate({
                  margins: {
                    ...design.template.margins,
                    top: parseInt(e.target.value)
                  }
                })}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-10">{design.template.margins.top}px</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Bottom</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="100"
                value={design.template.margins.bottom}
                onChange={(e) => onTemplateUpdate({
                  margins: {
                    ...design.template.margins,
                    bottom: parseInt(e.target.value)
                  }
                })}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-10">{design.template.margins.bottom}px</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Left</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="100"
                value={design.template.margins.left}
                onChange={(e) => onTemplateUpdate({
                  margins: {
                    ...design.template.margins,
                    left: parseInt(e.target.value)
                  }
                })}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-10">{design.template.margins.left}px</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Right</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="100"
                value={design.template.margins.right}
                onChange={(e) => onTemplateUpdate({
                  margins: {
                    ...design.template.margins,
                    right: parseInt(e.target.value)
                  }
                })}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-10">{design.template.margins.right}px</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            const value = 40;
            onTemplateUpdate({
              margins: {
                top: value,
                right: value,
                bottom: value,
                left: value
              }
            });
          }}
          className="mt-2 text-xs text-green-600 hover:text-green-700"
        >
          Reset to uniform margins
        </button>
      </div>

      {/* Text Spacing */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-4">Text Spacing</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Line Height</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="1"
                max="3"
                step="0.1"
                value={design.template.defaultTextStyle.lineHeight || 1.6}
                onChange={(e) => onTemplateUpdate({
                  defaultTextStyle: {
                    ...design.template.defaultTextStyle,
                    lineHeight: parseFloat(e.target.value)
                  }
                })}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-10">{design.template.defaultTextStyle.lineHeight || 1.6}</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Letter Spacing</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="-2"
                max="10"
                step="0.5"
                value={design.template.defaultTextStyle.letterSpacing || 0}
                onChange={(e) => onTemplateUpdate({
                  defaultTextStyle: {
                    ...design.template.defaultTextStyle,
                    letterSpacing: parseFloat(e.target.value)
                  }
                })}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-10">{design.template.defaultTextStyle.letterSpacing || 0}px</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Default Text Alignment</label>
            <div className="grid grid-cols-4 gap-1">
              {(['left', 'center', 'right', 'justify'] as const).map((align) => (
                <button
                  key={align}
                  onClick={() => onTemplateUpdate({
                    defaultTextStyle: {
                      ...design.template.defaultTextStyle,
                      textAlign: align
                    }
                  })}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    design.template.defaultTextStyle.textAlign === align
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {align.charAt(0).toUpperCase() + align.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            onTemplateUpdate({
              defaultTextStyle: {
                ...design.template.defaultTextStyle,
                lineHeight: 1.6,
                letterSpacing: 0,
                textAlign: 'center'
              }
            });
          }}
          className="mt-3 text-xs text-green-600 hover:text-green-700"
        >
          Reset to default values
        </button>
      </div>
    </div>
  );
};

export default LayoutTab;